import {useState} from 'react';
import {CustomerInvoiceFull, BillingLine, PaymentRecord} from '@/store/customerFile.ts';
import {ChevronDownIcon, ChevronUpIcon, ArrowDownTrayIcon} from '@heroicons/react/24/outline';
import {NewServiceCallModal} from '@/pages/service-request/components/NewServiceCallModal.tsx';

interface Props {
    invoices: CustomerInvoiceFull[];
}

function fmtCurrency(val: string | number | null | undefined) {
    if (val == null) return '—';
    const n = parseFloat(String(val));
    return `₪\u00a0${n.toLocaleString('he-IL', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
}

function fmtDate(dateStr: string | null | undefined) {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('he-IL', {day: '2-digit', month: '2-digit', year: 'numeric'});
}

function fmtMonthOfUse(dateStr: string | null | undefined) {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

const STATUS_STYLE: Record<number, {label: string; cls: string; valueCls: string}> = {
    1: {label: 'ממתין לתשלום', cls: 'bg-amber-100 text-amber-800', valueCls: 'text-amber-600'},
    2: {label: 'שולם', cls: 'bg-green-100 text-green-800', valueCls: 'text-green-600'},
    3: {label: 'בוטל', cls: 'bg-gray-100 text-gray-600', valueCls: 'text-gray-500'},
    4: {label: 'זוכה', cls: 'bg-blue-100 text-blue-700', valueCls: 'text-blue-600'},
    5: {label: 'שולם חלקית', cls: 'bg-yellow-100 text-yellow-800', valueCls: 'text-yellow-600'},
    6: {label: 'בעיה בתשלום', cls: 'bg-red-100 text-red-800', valueCls: 'text-red-600'},
    7: {label: 'זמנית לא לתשלום', cls: 'bg-gray-100 text-gray-500', valueCls: 'text-gray-500'},
};

function StatusPill({status}: {status: number}) {
    const s = STATUS_STYLE[status] ?? {label: '—', cls: 'bg-gray-100 text-gray-500', valueCls: ''};
    return (
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${s.cls}`}>
            {s.label}
        </span>
    );
}

function BillingLinesTable({lines}: {lines: BillingLine[]}) {
    if (lines.length === 0) {
        return <p className="text-xs text-gray-400 py-2">אין שורות בילינג</p>;
    }
    return (
        <div className="overflow-x-auto">
        <table className="w-full text-sm text-right">
            <thead>
            <tr className="border-b border-gray-200">
                <th className="py-1.5 px-2 text-xs font-medium text-gray-500">חודש שימוש</th>
                <th className="py-1.5 px-2 text-xs font-medium text-gray-500">מערכת</th>
                <th className="py-1.5 px-2 text-xs font-medium text-gray-500">סוג פריט</th>
                <th className="py-1.5 px-2 text-xs font-medium text-gray-500">כמות</th>
                <th className="py-1.5 px-2 text-xs font-medium text-gray-500">סה"כ לתשלום</th>
            </tr>
            </thead>
            <tbody>
            {lines.map((line) => (
                <tr key={line.id} className="border-b border-gray-50">
                    <td className="py-1.5 px-2 text-xs">{fmtMonthOfUse(line.month_of_use)}</td>
                    <td className="py-1.5 px-2 text-xs">{line.school_name ?? '—'}</td>
                    <td className="py-1.5 px-2 text-xs">{line.item_type_name}</td>
                    <td className="py-1.5 px-2 text-xs">
                        {line.units_quantity ? Number(line.units_quantity).toLocaleString('he-IL') : String(line.quantity)}
                    </td>
                    <td className="py-1.5 px-2 text-xs font-medium">{fmtCurrency(line.total_after_discount)}</td>
                </tr>
            ))}
            </tbody>
        </table>
        </div>
    );
}

function PaymentsTable({payments}: {payments: PaymentRecord[]}) {
    if (payments.length === 0) {
        return <p className="text-xs text-gray-400 py-2">אין תשלומים רשומים</p>;
    }
    return (
        <div className="overflow-x-auto">
        <table className="w-full text-sm text-right">
            <thead>
            <tr className="border-b border-gray-200">
                <th className="py-1.5 px-2 text-xs font-medium text-gray-500">סוג תשלום</th>
                <th className="py-1.5 px-2 text-xs font-medium text-gray-500">אמצעי תשלום</th>
                <th className="py-1.5 px-2 text-xs font-medium text-gray-500">סכום</th>
                <th className="py-1.5 px-2 text-xs font-medium text-gray-500">תאריך</th>
                <th className="py-1.5 px-2 text-xs font-medium text-gray-500">סטטוס</th>
                <th className="py-1.5 px-2 text-xs font-medium text-gray-500">מספר טרנזקציה</th>
                <th className="py-1.5 px-2 text-xs font-medium text-gray-500">סיבת כשל / פרטי חשב</th>
            </tr>
            </thead>
            <tbody>
            {payments.map((p) => (
                <tr key={p.id} className="border-b border-gray-50">
                    <td className="py-1.5 px-2 text-xs">{p.payment_type_display}</td>
                    <td className="py-1.5 px-2 text-xs font-mono">{p.account_details ?? p.last_4_digits ?? '—'}</td>
                    <td className="py-1.5 px-2 text-xs font-medium">{fmtCurrency(p.amount)}</td>
                    <td className="py-1.5 px-2 text-xs">{fmtDate(p.date)}</td>
                    <td className="py-1.5 px-2 text-xs">
                        <span className={p.status ? 'text-green-600' : 'text-red-600'}>
                            {p.status_label}
                        </span>
                    </td>
                    <td className="py-1.5 px-2 text-xs font-mono">{p.tranzaction_id ?? '—'}</td>
                    <td className="py-1.5 px-2 text-xs text-gray-500">{p.credit_reason ?? '—'}</td>
                </tr>
            ))}
            </tbody>
        </table>
        </div>
    );
}

function InvoiceCard({invoice}: {invoice: CustomerInvoiceFull}) {
    const [open, setOpen] = useState(false);
    const statusInfo = STATUS_STYLE[invoice.status];
    const isPaid = invoice.status === 2;

    return (
        <div className="border border-gray-200 rounded-xl mb-2 overflow-hidden">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-start justify-between gap-3 px-4 py-3 text-right bg-white hover:bg-gray-50 transition-colors"
            >
                <div className="flex items-start gap-3 flex-wrap">
                    <strong className="text-sm font-semibold text-gray-900">
                        {invoice.invoice_number ?? `חשבונית #${invoice.id}`}
                    </strong>
                    <StatusPill status={invoice.status} />
                    <span className="text-sm text-gray-700">סכום: {fmtCurrency(invoice.total_amount)}</span>
                    <span className="text-sm text-gray-500">פריטים: {invoice.billing_lines.length}</span>
                    <span className={`text-sm ${statusInfo?.valueCls ?? ''}`}>
                        סטטוס תשלום: {statusInfo?.label ?? invoice.status_display}
                    </span>
                    {invoice.last_payment && (
                        <span className="text-sm text-gray-500">
                            ניסיון תשלום אחרון: {fmtDate(invoice.last_payment)}
                        </span>
                    )}
                </div>
                <span className="shrink-0 text-gray-400 mt-0.5">
                    {open ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
                </span>
            </button>

            {open && (
                <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-3 space-y-4">
                    {isPaid && invoice.invoice_url && (
                        <div className="flex items-center gap-2">
                            <a
                                href={invoice.invoice_url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                            >
                                <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                                קישור לחשבונית
                            </a>
                        </div>
                    )}
                    <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">שורות בילינג / פריטים</p>
                        <BillingLinesTable lines={invoice.billing_lines} />
                    </div>
                    <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">תשלומים</p>
                        <PaymentsTable payments={invoice.payments} />
                    </div>
                </div>
            )}
        </div>
    );
}

function exportToExcel(invoices: CustomerInvoiceFull[]) {
    // Build CSV with BOM for Hebrew Excel support
    const rows: string[][] = [];
    rows.push(['מספר חשבונית', 'סטטוס', 'סכום', 'ניסיון תשלום אחרון', 'מערכת', 'סוג פריט', 'חודש שימוש', 'כמות', 'סה"כ פריט']);
    for (const inv of invoices) {
        const statusLabel = STATUS_STYLE[inv.status]?.label ?? inv.status_display;
        if (inv.billing_lines.length === 0) {
            rows.push([
                inv.invoice_number ?? `#${inv.id}`,
                statusLabel,
                String(inv.total_amount ?? ''),
                inv.last_payment ?? '',
                '', '', '', '', '',
            ]);
        } else {
            for (const line of inv.billing_lines) {
                rows.push([
                    inv.invoice_number ?? `#${inv.id}`,
                    statusLabel,
                    String(inv.total_amount ?? ''),
                    inv.last_payment ?? '',
                    line.school_name ?? '',
                    line.item_type_name,
                    line.month_of_use ?? '',
                    line.units_quantity != null ? String(line.units_quantity) : String(line.quantity),
                    String(line.total_after_discount ?? ''),
                ]);
            }
        }
    }
    const bom = '\uFEFF';
    const csv = bom + rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'חשבוניות.csv';
    a.click();
    URL.revokeObjectURL(url);
}

export function CustomerFileInvoicesFull({invoices}: Props) {
    const [openNewServiceCall, setOpenNewServiceCall] = useState(false);

    return (
        <div>
            {/* Toolbar above list */}
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <button
                    type="button"
                    onClick={() => setOpenNewServiceCall(true)}
                    className="text-sm text-blue-600 hover:underline"
                >
                    פתח פניה בנושא חשבונית
                </button>
                <button
                    type="button"
                    onClick={() => exportToExcel(invoices)}
                    disabled={invoices.length === 0}
                    className="inline-flex items-center gap-1.5 text-xs border border-gray-300 hover:border-gray-400 text-gray-700 bg-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                    ייצוא לאקסל
                </button>
            </div>

            <NewServiceCallModal
                open={openNewServiceCall}
                onOpenChange={setOpenNewServiceCall}
                defaultDepartment="36"
            />

            {invoices.length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center">אין חשבוניות רשומות</p>
            ) : (
                invoices.map((invoice) => (
                    <InvoiceCard key={invoice.id} invoice={invoice} />
                ))
            )}
        </div>
    );
}

